import { useEffect, useRef, useState } from 'react';
import { useDmpTranslation } from '@/hooks/useDmpTranslation';
import styled from 'styled-components';
import { Button } from '@/components/UI/Button';
import { theme } from '@/theme/Theme';
import { H4 } from '@/components/UI/Typography/Heading';
import { getAiMediaStatus, launchAiMedia, AiMediaStatus } from '@/api/aiMedia.api';
import { aiMediaErrorKey, AiMediaErrorKey, validateAiMediaLaunch } from '@/utils/aiMedia';
import { errorHandler } from '@/utils/toastHandler';

const Container = styled.section`
  width: 100%; max-width: 1215px; box-sizing: border-box;
  background: white; padding: 24px 32px; border-radius: ${theme.radii.lg};
  color: ${theme.colors.gray500};
  font-size: ${(p) => p.theme.fontSizes.base}; line-height: 1.6;
  .title { font-size: ${theme.fontSizes.md}; font-weight: 700; }
  .title.visually-hidden { position: absolute; width: 1px; height: 1px; margin: -1px; overflow: hidden;
    clip: rect(0, 0, 0, 0); white-space: nowrap; }
  p { margin: 8px 0 16px; }
  .status { background: #f5f7fc; border-left: 3px solid ${theme.colors.primary500}; padding: 12px 16px; }
  .actions { display: flex; flex-wrap: wrap; gap: 12px; }
  button { min-height: 44px; height: auto; white-space: normal; max-width: 100%; }
  @media (max-width: 640px) { padding: 16px; }
`;

// standalone：獨立頁已有同名頁面標題，卡片標題只保留給輔助科技。
export default function AiMediaLaunchCard({ standalone = false }: { standalone?: boolean }) {
  const { t } = useDmpTranslation('aiMedia');
  const [status, setStatus] = useState<AiMediaStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState<AiMediaErrorKey | ''>('');
  const inFlight = useRef(false);
  const mounted = useRef(true);
  const popup = useRef<Window | null>(null);

  const refresh = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getAiMediaStatus();
      if (!response.data.success) throw new Error('status_failed');
      if (mounted.current) setStatus(response.data.data);
    } catch (err) {
      if (mounted.current) { setStatus(null); setError(aiMediaErrorKey(err)); }
    } finally { if (mounted.current) setLoading(false); }
  };

  useEffect(() => {
    mounted.current = true;
    void refresh();
    return () => { mounted.current = false; popup.current?.close(); };
  }, []);

  const open = async () => {
    if (inFlight.current || !status?.available || !status.launch_origin) return;
    // Must happen in the click handler before the first await (Safari popup policy).
    const tab = window.open('', '_blank');
    if (!tab) { errorHandler(t('popup_blocked')); return; }
    tab.opener = null;
    tab.document.title = t('opening');
    tab.document.body.textContent = t('opening');
    popup.current = tab;
    inFlight.current = true;
    setOpening(true);
    try {
      const response = await launchAiMedia();
      if (!response.data.success) throw new Error('launch_failed');
      const url = validateAiMediaLaunch(response.data.data, status.launch_origin);
      if (!mounted.current || tab.closed) return;
      tab.location.replace(url);
    } catch (err) {
      tab.close();
      if (mounted.current) { errorHandler(t(aiMediaErrorKey(err))); void refresh(); }
    } finally {
      popup.current = null;
      inFlight.current = false;
      if (mounted.current) setOpening(false);
    }
  };

  const reason = status?.code === 'quota_exhausted' || status?.code === 'free_quota_contract_pending'
    || status?.code === 'profile_incomplete' ? status.code : 'unavailable';

  return <Container aria-labelledby="ai-media-title">
    <H4 id="ai-media-title" className={standalone ? 'title visually-hidden' : 'title'}>{t('title')}</H4>
    <p>{t('description')}</p>
    {process.env.REACT_APP_RELEASE_TARGET === 'production' && <p className="status" role="status">{t('pending_external_access')}</p>}
    <p className="status" role="status" aria-live="polite">{loading ? t('loading') : error ? t(error) : status?.available ? t('ready') : t(reason)}</p>
    <div className="actions">
      <Button type="button" variant="primary" disabled={loading || opening || !status?.available || !status.launch_origin} onClick={open}>
        {t(opening ? 'opening' : 'open')}
      </Button>
      {!loading && !opening && (!status?.available || error) && <Button type="button" variant="primary-invert" onClick={refresh}>{t('retry')}</Button>}
    </div>
  </Container>;
}
